import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import MakebaLogo from "./MakebaLogo";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list — check your inbox!");
      setEmail("");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="mt-0 bg-white border-t border-[#E5E2DA]">
      {/* Newsletter band */}
      <section className="border-b border-[#E5E2DA]">
        <div className="container-x py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">Stay in the loop</div>
            <h2 className="font-display font-light text-3xl md:text-4xl text-black tracking-tight">
              Join the crew. <span className="font-black">Never miss a drop.</span>
            </h2>
            <p className="mt-3 text-[#4A4A4A] font-light">
              New products, custom-order openings, and stories from the studio.
            </p>
          </div>
          <form onSubmit={submit} className="flex gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              data-testid="newsletter-email"
              className="flex-1 border border-black px-4 py-3 text-sm focus:outline-none focus:border-[#EC008C]"
            />
            <button data-testid="newsletter-submit" disabled={loading} className="btn-primary">
              {loading ? "..." : <>Subscribe <ArrowRight size={14} /></>}
            </button>
          </form>
        </div>
      </section>

      <div className="container-x py-16 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <MakebaLogo height={40} tagline={true} />
          <p className="mt-6 text-sm text-[#4A4A4A] max-w-md leading-relaxed font-light">
            Oklahoma-born, Mississippi-rooted. Custom apparel, character backpacks, and print work
            that celebrates culture, representation, and creativity.
          </p>
          <div className="mt-6 flex items-center gap-4">
            <a href="https://instagram.com/makebadesigns" target="_blank" rel="noreferrer" className="text-black hover:text-[#EC008C]">
              <Instagram size={18} />
            </a>
            <a href="https://facebook.com/makebadesigns" target="_blank" rel="noreferrer" className="text-black hover:text-[#EC008C]">
              <Facebook size={18} />
            </a>
            <a href="mailto:hello@makebadesigns.com" className="text-black hover:text-[#EC008C]">
              <Mail size={18} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-[11px] tracking-[0.28em] uppercase text-black mb-5">Shop</h4>
          <ul className="space-y-2.5 text-sm font-light text-[#4A4A4A]">
            <li><Link data-testid="footer-shop-backpacks" to="/shop?category=backpacks" className="hover:text-[#EC008C]">Backpacks</Link></li>
            <li><Link data-testid="footer-shop-tshirts" to="/shop?category=tshirts" className="hover:text-[#EC008C]">T-Shirts</Link></li>
            <li><Link data-testid="footer-shop-travel" to="/shop?category=travel" className="hover:text-[#EC008C]">Travel Bags</Link></li>
            <li><Link data-testid="footer-shop-hbcu" to="/shop?category=hbcu" className="hover:text-[#EC008C]">HBCU</Link></li>
            <li><Link data-testid="footer-shop-kids" to="/shop?category=kids" className="hover:text-[#EC008C]">Kids & Puzzles</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-[11px] tracking-[0.28em] uppercase text-black mb-5">Studio</h4>
          <ul className="space-y-2.5 text-sm font-light text-[#4A4A4A]">
            <li><Link data-testid="footer-designer" to="/designer" className="hover:text-[#EC008C]">Design Studio</Link></li>
            <li><Link to="/contact" className="hover:text-[#EC008C]">Custom Orders</Link></li>
            <li><Link to="/contact" className="hover:text-[#EC008C]">Wholesale</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-[11px] tracking-[0.28em] uppercase text-black mb-5">Contact</h4>
          <ul className="space-y-2.5 text-sm font-light text-[#4A4A4A]">
            <li>hello@makebadesigns.com</li>
            <li>@makebadesigns</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#E5E2DA] py-6">
        <div className="container-x flex justify-between items-center text-xs text-[#4A4A4A] font-light flex-wrap gap-3">
          <div>© {new Date().getFullYear()} Makeba Design & Prints, LLC. All rights reserved.</div>
          <div className="flex gap-6">
            <a href="/contact" className="hover:text-[#EC008C]">Contact</a>
            <a href="/contact" className="hover:text-[#EC008C]">Shipping</a>
            <a href="/contact" className="hover:text-[#EC008C]">Returns</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
